import type {
  Usuario,
  AuthResponse,
  AtlasCaso,
  MatrizMorfologica,
  AtlasGlitch,
  AtlasTactica,
  AtlasBibliografia,
  AtlasArchivoVisual,
  Capitulo,
  AtlasHito,
  FuenteEnlace,
  BackupData,
  EstadoTarea,
  AtlasEncuadre,
  AtlasObjetivoEspecifico
} from '../types/index.ts';

const TOKEN_KEY = 'aurora_auth_token';

// Interceptor global para detección inmediata de tokens expirados o revocados
if (typeof window !== 'undefined' && !(window as any).__doctoral_fetch_intercepted__) {
  (window as any).__doctoral_fetch_intercepted__ = true;
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const res = await originalFetch.apply(this, args);
    const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url || '';
    if (res.status === 401 && url.includes('/api/') && !url.includes('/api/auth/login')) {
      localStorage.removeItem(TOKEN_KEY);
      window.dispatchEvent(new CustomEvent('doctoral:session_expired'));
    }
    return res;
  };
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export const Api = {
  // --- AUTENTICACIÓN ---
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },

  setToken(token: string) {
    localStorage.setItem(TOKEN_KEY, token);
  },

  clearToken() {
    localStorage.removeItem(TOKEN_KEY);
  },

  async login(username: string, password: string): Promise<AuthResponse> {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Credenciales inválidas');
    }
    if (data.token) {
      Api.setToken(data.token);
    }
    return data;
  },

  async getMe(): Promise<{ authenticated: boolean; user?: Usuario }> {
    const token = Api.getToken();
    if (!token) return { authenticated: false };

    try {
      const res = await fetch('/api/auth/me', {
        headers: getAuthHeaders()
      });
      if (!res.ok) return { authenticated: false };
      return res.json();
    } catch {
      return { authenticated: false };
    }
  },

  async logout(): Promise<void> {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: getAuthHeaders()
      });
    } catch {
      // Ignorar error al cerrar sesión
    }
    Api.clearToken();
  },

  // --- 0. ENCUADRE DOCTORAL (HIPÓTESIS Y OBJETIVOS) ---
  async getEncuadre(): Promise<{ encuadre: AtlasEncuadre | null; objetivos: AtlasObjetivoEspecifico[] }> {
    const res = await fetch('/api/encuadre', { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Error al cargar encuadre doctoral');
    return res.json();
  },

  async updateEncuadre(encuadre: Partial<AtlasEncuadre>): Promise<{ ok: boolean; encuadre: AtlasEncuadre }> {
    const res = await fetch('/api/encuadre', {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(encuadre)
    });
    if (!res.ok) throw new Error('Error al actualizar encuadre doctoral');
    return res.json();
  },

  async addObjetivoEspecifico(oe: Partial<AtlasObjetivoEspecifico>): Promise<{ ok: boolean; id: number }> {
    const res = await fetch('/api/encuadre/objetivos', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(oe)
    });
    if (!res.ok) throw new Error('Error al crear objetivo específico');
    return res.json();
  },

  async updateObjetivoEspecifico(id: number | string, oe: Partial<AtlasObjetivoEspecifico>): Promise<{ ok: boolean; id: number }> {
    const res = await fetch(`/api/encuadre/objetivos/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(oe)
    });
    if (!res.ok) throw new Error('Error al actualizar objetivo específico');
    return res.json();
  },

  async deleteObjetivoEspecifico(id: number | string): Promise<{ ok: boolean }> {
    const res = await fetch(`/api/encuadre/objetivos/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Error al eliminar objetivo específico');
    return res.json();
  },

  // --- 1. CASOS DE ESTUDIO ---
  async getCasos(): Promise<AtlasCaso[]> {
    const res = await fetch('/api/casos', { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Error al cargar casos de estudio');
    return res.json();
  },

  async addCaso(caso: Partial<AtlasCaso>): Promise<{ ok: boolean; id: number }> {
    const res = await fetch('/api/casos', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(caso)
    });
    if (!res.ok) throw new Error('Error al guardar caso de estudio');
    return res.json();
  },

  // --- 2. MATRIZ MORFOTIPOLÓGICA (OE1) ---
  async getMatrizMorfologica(): Promise<MatrizMorfologica[]> {
    const res = await fetch('/api/matriz-morfologica', { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Error al cargar matriz morfológica');
    return res.json();
  },

  async addMatrizMorfologica(item: Partial<MatrizMorfologica>): Promise<{ ok: boolean; id: number }> {
    const res = await fetch('/api/matriz-morfologica', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(item)
    });
    if (!res.ok) throw new Error('Error al registrar matriz morfológica');
    return res.json();
  },

  async updateMatrizMorfologica(id: number | string, item: Partial<MatrizMorfologica>): Promise<{ ok: boolean; id: number }> {
    const res = await fetch(`/api/matriz-morfologica/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(item)
    });
    if (!res.ok) throw new Error('Error al actualizar registro de matriz morfológica');
    return res.json();
  },

  async deleteMatrizMorfologica(id: number | string): Promise<{ ok: boolean }> {
    const res = await fetch(`/api/matriz-morfologica/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Error al eliminar registro');
    return res.json();
  },

  // --- 3. DIMENSIONES DEL GLITCH & IGU (OE2) ---
  async getGlitches(): Promise<AtlasGlitch[]> {
    const res = await fetch('/api/glitches', { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Error al cargar glitches desde SQLite');
    return res.json();
  },

  async addGlitch(glitch: Partial<AtlasGlitch>): Promise<{ ok: boolean; id: number; igu: number }> {
    const res = await fetch('/api/glitches', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(glitch)
    });
    if (!res.ok) throw new Error('Error al registrar glitch en SQLite');
    return res.json();
  },

  async updateGlitch(id: number | string, glitch: Partial<AtlasGlitch>): Promise<{ ok: boolean; id: number }> {
    const res = await fetch(`/api/glitches/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(glitch)
    });
    if (!res.ok) throw new Error('Error al actualizar glitch en SQLite');
    return res.json();
  },

  async deleteGlitch(id: number | string): Promise<{ ok: boolean }> {
    const res = await fetch(`/api/glitches/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Error al eliminar glitch de SQLite');
    return res.json();
  },

  // --- 4. REPERTORIOS DE RESISTENCIA TÁCTICA (OE3) ---
  async getTacticas(): Promise<AtlasTactica[]> {
    const res = await fetch('/api/tacticas', { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Error al cargar tácticas');
    return res.json();
  },

  async addTactica(tactica: Partial<AtlasTactica>): Promise<{ ok: boolean; id: number }> {
    const res = await fetch('/api/tacticas', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(tactica)
    });
    if (!res.ok) throw new Error('Error al registrar táctica');
    return res.json();
  },

  async updateTactica(id: number | string, tactica: Partial<AtlasTactica>): Promise<{ ok: boolean; id: number }> {
    const res = await fetch(`/api/tacticas/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(tactica)
    });
    if (!res.ok) throw new Error('Error al actualizar táctica en SQLite');
    return res.json();
  },

  async deleteTactica(id: number | string): Promise<{ ok: boolean }> {
    const res = await fetch(`/api/tacticas/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Error al eliminar táctica');
    return res.json();
  },

  // --- 5. FICHERO TEÓRICO & APA 7 ---
  async getBibliografia(): Promise<AtlasBibliografia[]> {
    const res = await fetch('/api/bibliografia', { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Error al cargar bibliografía APA');
    return res.json();
  },

  async addBibliografia(ref: Partial<AtlasBibliografia>): Promise<{ ok: boolean; id: number }> {
    const res = await fetch('/api/bibliografia', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(ref)
    });
    if (!res.ok) throw new Error('Error al registrar bibliografía APA');
    return res.json();
  },

  async updateBibliografia(id: number | string, ref: Partial<AtlasBibliografia>): Promise<{ ok: boolean; id: number }> {
    const res = await fetch(`/api/bibliografia/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(ref)
    });
    if (!res.ok) throw new Error('Error al actualizar bibliografía APA');
    return res.json();
  },

  async deleteBibliografia(id: number | string): Promise<{ ok: boolean }> {
    const res = await fetch(`/api/bibliografia/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Error al eliminar referencia APA');
    return res.json();
  },

  // Compatibilidad hacia atrás citas
  async getCitas(): Promise<AtlasBibliografia[]> {
    return Api.getBibliografia();
  },
  async addCita(cita: any): Promise<{ ok: boolean; id: any }> {
    return Api.addBibliografia(cita);
  },
  async deleteCita(id: string | number): Promise<{ ok: boolean }> {
    return Api.deleteBibliografia(id);
  },

  // --- 6. ARCHIVO VISUAL & PLANIMETRÍA ---
  async getArchivosVisuales(): Promise<AtlasArchivoVisual[]> {
    const res = await fetch('/api/archivos-visuales', { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Error al cargar archivo visual');
    return res.json();
  },

  async addArchivoVisual(item: Partial<AtlasArchivoVisual>): Promise<{ ok: boolean; id: number }> {
    const res = await fetch('/api/archivos-visuales', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(item)
    });
    if (!res.ok) throw new Error('Error al registrar elemento visual');
    return res.json();
  },

  async updateArchivoVisual(id: number | string, item: Partial<AtlasArchivoVisual>): Promise<{ ok: boolean; id: number }> {
    const res = await fetch(`/api/archivos-visuales/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(item)
    });
    if (!res.ok) throw new Error('Error al actualizar elemento visual en SQLite');
    return res.json();
  },

  async deleteArchivoVisual(id: number | string): Promise<{ ok: boolean }> {
    const res = await fetch(`/api/archivos-visuales/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Error al eliminar elemento visual');
    return res.json();
  },

  // --- 7. REDACCIÓN CAPITULAR ---
  async getCapitulos(): Promise<Capitulo[]> {
    const res = await fetch('/api/capitulos', { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Error al cargar capítulos desde SQLite');
    return res.json();
  },

  async addCapitulo(capitulo: { name?: string; titulo?: string; numero?: number; contenido_html?: string; estado_edicion?: string }): Promise<{ ok: boolean; id: number; numero: number; name: string }> {
    const res = await fetch('/api/capitulos', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(capitulo)
    });
    if (!res.ok) throw new Error('Error al crear capítulo en SQLite');
    return res.json();
  },

  async updateCapitulo(id: string | number, contenido_html: string, name?: string, estado_edicion: string = 'borrador'): Promise<{ ok: boolean; actualizado_en: string; palabras_totales: number }> {
    const res = await fetch(`/api/capitulos/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ contenido_html, name, estado_edicion })
    });
    if (!res.ok) throw new Error('Error al guardar capítulo en SQLite');
    return res.json();
  },

  async deleteCapitulo(id: string | number): Promise<{ ok: boolean; message: string }> {
    const res = await fetch(`/api/capitulos/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Error al eliminar capítulo de SQLite');
    return res.json();
  },

  // --- 8. CRONOGRAMA DE INVESTIGACIÓN (HITOS / TAREAS) ---
  async getHitos(): Promise<AtlasHito[]> {
    const res = await fetch('/api/hitos', { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Error al cargar hitos doctorales');
    return res.json();
  },

  async addHito(hito: Partial<AtlasHito>): Promise<{ ok: boolean; id: number }> {
    const res = await fetch('/api/hitos', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(hito)
    });
    if (!res.ok) throw new Error('Error al crear hito doctoral');
    return res.json();
  },

  async updateHito(id: number | string, hito: Partial<AtlasHito>): Promise<{ ok: boolean; id: number }> {
    const res = await fetch(`/api/hitos/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(hito)
    });
    if (!res.ok) throw new Error('Error al actualizar hito doctoral en SQLite');
    return res.json();
  },

  async updateHitoEstado(id: number | string, estado: string, avance?: number): Promise<{ ok: boolean }> {
    const res = await fetch(`/api/hitos/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ estado, avance })
    });
    if (!res.ok) throw new Error('Error al actualizar estado del hito');
    return res.json();
  },

  async deleteHito(id: number | string): Promise<{ ok: boolean }> {
    const res = await fetch(`/api/hitos/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Error al eliminar hito de SQLite');
    return res.json();
  },

  // Compatibilidad hacia atrás tareas
  async getTareas(): Promise<AtlasHito[]> {
    return Api.getHitos();
  },
  async addTarea(tarea: any): Promise<{ ok: boolean; id: any }> {
    return Api.addHito(tarea);
  },
  async updateTareaEstado(id: string | number, estado: EstadoTarea): Promise<{ ok: boolean }> {
    return Api.updateHitoEstado(id, estado);
  },
  async deleteTarea(id: string | number): Promise<{ ok: boolean }> {
    return Api.deleteHito(id);
  },

  // --- 9. GEOPORTALES & ENLACES ---
  async getEnlaces(): Promise<FuenteEnlace[]> {
    const res = await fetch('/api/enlaces', { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Error al cargar fuentes y geoportales');
    return res.json();
  },

  async addEnlace(enlace: Partial<FuenteEnlace>): Promise<{ ok: boolean; id: number }> {
    const res = await fetch('/api/enlaces', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(enlace)
    });
    if (!res.ok) throw new Error('Error al registrar enlace en SQLite');
    return res.json();
  },

  async deleteEnlace(id: string | number): Promise<{ ok: boolean }> {
    const res = await fetch(`/api/enlaces/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Error al eliminar fuente');
    return res.json();
  },

  // --- 10. BACKUP TOTAL RELACIONAL ---
  async exportBackup(): Promise<BackupData> {
    const res = await fetch('/api/backup/export', { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Error al generar backup desde SQLite');
    return res.json();
  },

  async importBackup(data: BackupData): Promise<{ ok: boolean; message: string }> {
    const res = await fetch('/api/backup/import', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Error al restaurar base de datos');
    }
    return res.json();
  }
};
